import React, { useState, useCallback, useMemo } from "react";
import DataTable from "react-data-table-component";
import Loader from "../Loader";
import { format } from "date-fns";
import { useUser } from "../../contexts/UserContext";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";

const CustomPagination = ({ currentPage, rowCount, rowsPerPage, onChangePage }) => {
  if (!rowCount || rowCount === 0) return null;
  const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, rowCount);

  const getPageNumbers = () => {
    const pages = [];
    const isMobile = window.innerWidth < 640;
    const maxVisible = isMobile ? 3 : 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    startPage = Math.max(1, startPage);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
      <div className="text-sm text-gray-600 order-2 sm:order-1">
        Showing <span className="font-semibold">{start}-{end}</span> of <span className="font-semibold">{rowCount}</span>
      </div>
      <div className="flex gap-1 sm:gap-2 items-center order-1 sm:order-2">
        <button
          type="button"
          onClick={() => onChangePage(1)}
          disabled={currentPage <= 1}
          className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center"
        >«</button>
        {getPageNumbers().map((pageNum) => (
          <button
            key={pageNum}
            type="button"
            onClick={() => onChangePage(pageNum)}
            className={`min-w-[32px] h-[32px] flex items-center justify-center text-xs border rounded font-medium ${currentPage === pageNum ? "bg-red-600 text-white border-red-600 shadow-sm" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-100"}`}
          >{pageNum}</button>
        ))}
        <button
          type="button"
          onClick={() => onChangePage(totalPages)}
          disabled={currentPage >= totalPages}
          className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-100 bg-white min-w-[32px] h-[32px] flex items-center justify-center"
        >»</button>
      </div>
    </div>
  );
};

const getStatusBadgeClass = (status_id) => {
  const map = {
    1: "bg-blue-50 text-blue-600 border-blue-100", // New
    2: "bg-purple-50 text-purple-600 border-purple-100", // Under Discussion
    3: "bg-amber-50 text-amber-600 border-amber-100", // Under Process & Design
    4: "bg-indigo-50 text-indigo-600 border-indigo-100", // Under UI/UX Design
    5: "bg-violet-50 text-violet-600 border-violet-100", // SME Review
    6: "bg-pink-50 text-pink-600 border-pink-100", // Pending Client Design Approval
    7: "bg-emerald-50 text-emerald-600 border-emerald-100", // Design Approved
    8: "bg-teal-50 text-teal-600 border-teal-100", // Req Freezed
    9: "bg-rose-50 text-rose-600 border-rose-100", // Pending Client Verification
    10: "bg-green-50 text-green-600 border-green-100", // Client Approved
    11: "bg-sky-50 text-sky-600 border-sky-100", // Completed
    12: "bg-gray-50 text-gray-500 border-gray-100", // Closed
    13: "bg-orange-50 text-orange-600 border-orange-100", // Rework Required
    14: "bg-slate-50 text-slate-500 border-slate-200", // On Hold
    15: "bg-red-50 text-red-600 border-red-100", // Rejected
  };
  return map[status_id] || "bg-gray-100 text-gray-800 border-gray-200";
};

const getStatusShortCode = (status_name) => {
    const map = {
        'New': 'NEW',
        'Under Discussion': 'UNDR DISC',
        'Under Process and Design Confirmation': 'PROC & DSGN',
        'Under UI/UX Design': 'UI/UX',
        'SME Review': 'SME REV',
        'Pending Client Design Approval': 'PND DSGN APPR',
        'Design Approved': 'DSGN APPR',
        'Requirement Freezed and moved to development': 'REQ FRZ / DEV',
        'Pending Client Verification': 'PND CLNT VER',
        'Client Approved': 'CLNT APPR',
        'Completed': 'CMPLTD',
        'Closed': 'CLOSED',
        'Rework Required': 'REWORK',
        'On Hold': 'HOLD',
        'Rejected': 'RJCTD'
    };
    return map[status_name] || status_name;
};

const getPriorityBadgeClass = (priority_id) => {
    const map = {
        1: "bg-green-100 text-green-800",
        2: "bg-blue-100 text-blue-800",
        3: "bg-orange-100 text-orange-800",
        4: "bg-red-100 text-red-800",
    }
    return map[priority_id] || "bg-gray-100 text-gray-800";
}

const getPriorityLabel = (priority_id) => {
    const map = {
        1: "Low",
        2: "Medium",
        3: "High",
        4: "Critical",
    }
    return map[priority_id] || "Unknown";
}

export default function ProjectManagementTable({
  tasks,
  onTaskClick,
  isLoading,
  totalRows,
  currentPage,
  rowsPerPage,
  onPageChange,
  onEdit,
  onDelete,
}) {
  const { user } = useUser();
  const isSuperAdmin = String(user?.role_id) === '1';
  const isInternal = String(user?.role_id) !== '2';

  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "TASK ID",
        selector: (row) => row.id,
        sortable: true,
        width: "100px",
        cell: (row) => (
          <div className="flex flex-col items-start gap-1">
            <span className="font-bold text-gray-800">#{row.id}</span>
            {row.is_draft == 1 && (
              <span className="bg-slate-700 text-white text-[7px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest animate-pulse border border-slate-500 shadow-sm flex items-center gap-1 shrink-0">
                <div className="w-1 h-1 rounded-full bg-slate-300" />
                Draft
              </span>
            )}
          </div>
        )
      },
      {
        name: "PROJECT NAME / CUSTOMER",
        selector: (row) => row.project_name,
        sortable: true,
        grow: 2,
        minWidth: "350px",
        cell: (row) => (
          <div className="flex flex-col py-2 w-full h-full justify-center" data-tag="allowRowEvents">
            <span className="font-bold text-gray-900 truncate tracking-tight uppercase leading-tight" data-tag="allowRowEvents">{row.project_name}</span>
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter mt-0.5" data-tag="allowRowEvents">{row.customer_name}</span>
          </div>
        )
      },
      {
        name: "SYSTEM MODULE",
        selector: (row) => row.system_modules,
        sortable: true,
        width: "180px",
        cell: (row) => (
          <div className="flex flex-wrap items-center w-full h-full py-2 gap-1" data-tag="allowRowEvents">
            <span className="text-[10px] font-black text-main bg-blue-50/50 px-2 py-1 rounded whitespace-normal leading-relaxed pointer-events-none border border-blue-100/50 inline-block" data-tag="allowRowEvents" title={row.system_modules}>
              {row.system_modules || 'N/A'}
            </span>
          </div>
        )
      },
      {
        name: "VERTICAL HEAD",
        selector: (row) => row.vh_first_name,
        sortable: true,
        grow: 1,
        minWidth: "180px",
        cell: (row) => (
          <div className="flex items-center w-full h-full py-2" data-tag="allowRowEvents">
            <span className="text-[11px] font-bold text-gray-700 pointer-events-none" data-tag="allowRowEvents">
              {row.vh_first_name ? `${row.vh_first_name} ${row.vh_last_name}` : '-'}
            </span>
          </div>
        )
      },
      {
          name: "ASSIGNED USERS",
          selector: (row) => row.assigned_users,
          sortable: true,
          width: "200px",
          cell: (row) => {
            const isClientReview = [6, 9].includes(Number(row.status_id));
            const displayAssignee = isClientReview ? `${row.customer_name}` : (row.assigned_users || 'Unassigned');
            
            return (
              <div className="flex items-center w-full h-full py-2" data-tag="allowRowEvents">
                  <span className={`text-[11px] font-bold truncate pointer-events-none ${isClientReview ? 'text-rose-600' : 'text-main'}`} data-tag="allowRowEvents" title={displayAssignee}>
                      {displayAssignee}
                  </span>
              </div>
            );
          }
      },
      {
        name: "PRIORITY",
        selector: (row) => row.priority_id,
        sortable: true,
        width: "100px",
        cell: (row) => (
          <div className="flex items-center w-full h-full py-2" data-tag="allowRowEvents">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase shadow-sm pointer-events-none ${getPriorityBadgeClass(row.priority_id)}`} data-tag="allowRowEvents">
              {getPriorityLabel(row.priority_id)}
            </span>
          </div>
        )
      },
      {
        name: "STATUS",
        selector: (row) => row.status_name,
        sortable: true,
        width: "150px",
        cell: (row) => (
          <div className="flex items-center w-full h-full py-2" data-tag="allowRowEvents">
            <span 
                className={`px-2 py-1 rounded-md text-[10px] font-black uppercase border shadow-sm pointer-events-none ${getStatusBadgeClass(row.status_id)}`} 
                data-tag="allowRowEvents"
                title={row.status_name}
            >
              {getStatusShortCode(row.status_name)}
            </span>
          </div>
        )
      },
      {
        name: "VERSION",
        selector: (row) => row.version_number,
        sortable: true,
        width: "100px",
        cell: (row) => (
          <div className="flex items-center w-full h-full py-2" data-tag="allowRowEvents">
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest pointer-events-none" data-tag="allowRowEvents">
              {row.version_number || '1.0.0'}
            </span>
          </div>
        )
      },
      {
        name: "TIMELINE",
        width: "160px",
        cell: (row) => (
          <div className="flex flex-col text-[10px] items-start py-2 w-full h-full justify-center" data-tag="allowRowEvents">
            {row.expected_start_date ? (
              <>
                <span className="text-gray-400 font-bold tracking-tighter pointer-events-none" data-tag="allowRowEvents">START: {format(new Date(row.expected_start_date), 'dd MMM yy')}</span>
                <span className="text-red-500 font-black tracking-tighter pointer-events-none" data-tag="allowRowEvents">END: {row.expected_completion_date ? format(new Date(row.expected_completion_date), 'dd MMM yy') : '-'}</span>
              </>
            ) : (
                <span className="text-gray-300 italic font-bold tracking-tighter pointer-events-none" data-tag="allowRowEvents">No timeline set</span>
            )}
          </div>
        )
      },
    ];

    if (isSuperAdmin) {
      baseColumns.push({
        name: "ACTIONS",
        width: "100px",
        center: true,
        cell: (row) => (
          <div className="flex items-center gap-1">
            <button 
              onClick={(e) => { e.stopPropagation(); onEdit?.(row); }}
              className="p-1.5 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
              title="Edit Requirement"
            >
              <PencilSquareIcon className="w-5 h-5" />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete?.(row.id); }}
              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete Requirement"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        )
      });
    }

    return baseColumns;
  }, [isSuperAdmin, onEdit, onDelete, tasks]);

  const customStyles = {
    headRow: { style: { backgroundColor: "#f9fafb", borderBottom: "2px solid #e5e7eb" } },
    rows: { style: { minHeight: "65px", cursor: "pointer", "&:hover": { backgroundColor: "#f3f4f6" } } },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px", cursor: "pointer" } },
    headCells: { style: { paddingLeft: "16px", paddingRight: "16px", fontWeight: "bold", textTransform: "uppercase", fontSize: "11px", color: "#4b5563" } }
  };

  return (
    <div className="w-full">
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
        <DataTable
          key={`pm-table-${totalRows}-${rowsPerPage}`}
          columns={columns}
          data={tasks}
          onRowClicked={onTaskClick}
          progressPending={isLoading}
          progressComponent={<div className="py-20"><Loader /></div>}
          customStyles={customStyles}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationPerPage={rowsPerPage}
          paginationRowsPerPageOptions={[5, 10, 25, 50, 100, 9999]}
          paginationDefaultPage={currentPage}
          onChangePage={onPageChange}
          paginationComponent={CustomPagination}
          noDataComponent={<div className="py-12 text-gray-500 text-center font-medium italic">No project tasks found.</div>}
          highlightOnHover
          pointerOnHover
        />
      </div>
    </div>
  );
}
